import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getDesign,
  sendChat,
  requestSignoff,
  saveThumbnail,
  getCredits,
  exportReel,
  mockPaymentSuccess,
  updateDesign,
} from "../lib/api";
import useStripeCheckoutPolling from "../hooks/useStripeCheckoutPolling";
import useChatController from "../hooks/useChatController";
import { startTelemetryFlush } from "../lib/interactionTelemetry";
import HeaderBar from "../components/HeaderBar";
import ChatDrawer from "../components/ChatDrawer";
import MobileChatDock from "../components/MobileChatDock";
import ThreeViewport from "../components/ThreeViewport";
import SignoffDialog from "../components/SignoffDialog";
import ViewOptionsPanel from "../components/ViewOptionsPanel";
import LoadingOverlay from "../components/LoadingOverlay";
import ShortcutsOverlay from "../components/ShortcutsOverlay";
import BlueprintLegend from "../components/BlueprintLegend";
import FloorPlan2D from "../components/FloorPlan2D";
import AIRendersPanel from "../components/AIRendersPanel";
import RecentlyDeletedDialog from "../components/RecentlyDeletedDialog";
import UploadBlueprintDialog from "../components/UploadBlueprintDialog";
import UploadHistoryDialog from "../components/UploadHistoryDialog";
import ShareTransformDialog from "../components/ShareTransformDialog";
import LiveInspectorRail from "../components/LiveInspectorRail";
import AdaAuditDialog from "../components/AdaAuditDialog";
import { applyAdaCompliance, revertAdaCompliance, bumpDoorToAda, auditAdaCompliance } from "../lib/adaMode";
import useCodeViolations from "../hooks/useCodeViolations";
import useIfcValidation from "../hooks/useIfcValidation";
import ArchitectHandoffDialog from "../components/ArchitectHandoffDialog";
import CodeViolationsDialog from "../components/CodeViolationsDialog";
import EnergyScoreDialog from "../components/EnergyScoreDialog";
import LocalCodeDialog from "../components/LocalCodeDialog";
import { FileSignature, ArrowLeft, BadgeCheck, Sparkles, LayoutPanelTop, Box, Image as ImageIcon, ToggleLeft, ToggleRight, Share2, Save, Loader2, ShieldCheck, Zap, MapPin, Undo2, Upload, History } from "lucide-react";
import { createShareLink, duplicateDesign, postAnalyticsEvent } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import { toast } from "sonner";
import {
  advanceBuildQueue,
  completeFinalPhase,
  countBlueprintRooms,
  diffFixtures,
  looksLikeDeleteIntent,
  buildTourWaypoints,
  mergeParsedBlueprint,
} from "../lib/editorHelpers";
import { computeBlueprintDiff } from "../lib/blueprintDiff";

// V1.5.3 Build Plan helpers + 4-waypoint tour builder live in
// /app/frontend/src/lib/editorHelpers.js — extracted in the V2.0.61
// monolith split.

export default function Editor() {  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  // V2.0.81 — chatLoading, previousBlueprintRef, chatLoadingTimeoutRef,
  // runChatTurn, and handleSend all extracted to `useChatController`.
  // Access via `chat.chatLoading` / `chat.previousBlueprintRef` etc.
  const [signoffOpen, setSignoffOpen] = useState(false);
  const [signoffLoading, setSignoffLoading] = useState(false);
  const [codeViolationsOpen, setCodeViolationsOpen] = useState(false);
  const [energyScoreOpen, setEnergyScoreOpen] = useState(false);
  const [localCodeOpen, setLocalCodeOpen] = useState(false);
  const [signoffResult, setSignoffResult] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [credits, setCredits] = useState(null);
  // V2.0.62 — admin allowlist bypass mirror. Backend `/me/credits` returns
  // `is_admin: true` for ADMIN_EMAILS users; we skip the paywall front-run
  // in `openSignoff` for them so "Email My Package" goes straight to send.
  const [isAdmin, setIsAdmin] = useState(false);
  const [floorLevels, setFloorLevels] = useState([]);
  const [visibleFloors, setVisibleFloors] = useState([]); // [] = ALL
  // V1.9.9 — controlled floor selection. Lifted out of FloorPlan2D so the
  // 3D viewport, persist writes, and the in-canvas floor switcher all
  // agree on which floor the user is editing.
  const [currentFloor, setCurrentFloor] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState(13); // hour 0..24
  // V2.0.30 — Lifted compliance fetch so both the LiveInspectorRail
  // (traffic-light) and FloorPlan2D (per-wall overlay) share a single
  // debounced source of truth.
  const { violationData, loading: loadingViolations } =
    useCodeViolations(id, design?.blueprint_json);
  // V2.0.39 — Pre-flight IFC validation lifted from LiveInspectorRail so
  // the Editor can fire the "Email it to your architect" toast after 5
  // consecutive clean ticks (architect-tier unlocks only).
  const { result: ifcValidation, loading: ifcValidationLoading } = useIfcValidation(
    id, design?.blueprint_json,
  );
  const cleanTickCountRef = useRef(0);
  const [architectHandoffOpen, setArchitectHandoffOpen] = useState(false);
  useEffect(() => {
    if (!ifcValidation || !design || !id) return;
    const unlock = design.project_unlock;
    const tier = unlock?.tier;
    const isArchitectTier = tier && tier !== "visualizer";
    if (!isArchitectTier) {
      cleanTickCountRef.current = 0;
      return;
    }
    if (!ifcValidation.passed) {
      cleanTickCountRef.current = 0;
      return;
    }
    // V2.0.39 — Architect-handoff toast fires the first time an
    // architect-tier user has a clean IFC tick on this design (one-shot
    // per design, persisted in localStorage). Threshold lowered from
    // "5 consecutive clean ticks" because the validation hook only
    // re-fires on architectural mutations — most paid users land with
    // a clean state and never see the toast otherwise.
    cleanTickCountRef.current += 1;
    if (cleanTickCountRef.current < 1) return;
    const flagKey = `architect-handoff-toast-shown:${id}`;
    try {
      if (localStorage.getItem(flagKey) === "1") return;
    } catch (err) { console.debug("[Editor] localStorage read failed:", err); }
    try { localStorage.setItem(flagKey, "1"); } catch (err) { console.debug("[Editor] localStorage write failed:", err); }
    // Small delay so the toast doesn't compete with the "design loaded"
    // confetti / initial render UI noise.
    const timer = setTimeout(() => {
      toast(
        "Your IFC is in perfect hand-off shape.",
        {
          description: "Want me to email the build-ready files to your architect?",
          duration: 14000,
          action: {
            label: "Email architect",
            onClick: () => setArchitectHandoffOpen(true),
          },
        },
      );
    }, 1800);
    return () => clearTimeout(timer);
  }, [ifcValidation, design, id]);
  const viewportRef = useRef(null);
  const [tourRunning, setTourRunning] = useState(false);
  const [reelExporting, setReelExporting] = useState(false);
  const capturedFramesRef = useRef([]);
  const frameTimerRef = useRef(null);
  const [enhanceLoading, setEnhanceLoading] = useState(false);
  const [showInteriorWalls, setShowInteriorWalls] = useState(true);
  const [xrayInteriorWalls, setXrayInteriorWalls] = useState(false);
  // V1.6 — Drafting Mode (Whiteprint aesthetic). Auto-engaged for mansions
  // (≥6 BD or ≥4000 sqft) on first design load; user can toggle thereafter.
  // The auto-on rule fires exactly once per design id via the ref guard.
  const [draftingMode, setDraftingMode] = useState(false);
  const draftingAutoFiredRef = useRef(false);
  // V1.7 — Simple Mode (default ON). Hides architectural/power-user UI so
  // plain-talk users get a frictionless flow. Forwarded to backend chat
  // requests so Gemini uses the tight prompt that doesn't time out.
  // V1.9.1 — LOCKED on. The architect-mode rebuild path crashes on large
  // designs; we'll re-enable behind a subscription gate later. The toggle
  // button has been removed and replaced with a status pill in the
  // // VIEW OPTIONS panel. `setSimpleMode` intentionally absent.
  const [simpleMode] = useState(true);
  // V2.0.57 — Quick Mode: passes lowDetail=true into runChatTurn so
  // responses stay short and beat Cloudflare's 100s edge timeout. The
  // toggle persists in localStorage; a 504/524 from the chat endpoint
  // auto-flips it ON and flashes the chip for ~2.5s to draw the eye.
  const [quickMode, setQuickMode] = useState(() => {
    try { return localStorage.getItem("blueprintai.quickMode") === "1"; } catch { return false; }
  });
  const [quickModeFlash, setQuickModeFlash] = useState(false);
  const toggleQuickMode = useCallback(() => {
    setQuickMode((v) => {
      const next = !v;
      try { localStorage.setItem("blueprintai.quickMode", next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  }, []);
  // V1.7 — Active canvas tab: "3d" or "plan". Default = "plan" so the
  // friendly 2D floor plan is what users see first; the 3D massing
  // diagram is a click away.
  const [canvasView, setCanvasView] = useState("plan");
  // V2.0.4 — Mobile/tablet awareness. Drives layout-level decisions:
  //   - mobile: lock canvasView to "plan" (3D too heavy + screen too small)
  //   - mobile: collapse the right-side action bar into an overflow menu
  //   - mobile: hide door/window status chips, symbol palette, PDF chip
  const { isMobile, isTablet } = useIsMobile();
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  // Lock canvasView to "plan" on phones (3D is too heavy + screen too small).
  useEffect(() => {
    if (isMobile && canvasView !== "plan") setCanvasView("plan");
  }, [isMobile, canvasView]);

  // V1.7 — AI Renders panel state.
  const [rendersOpen, setRendersOpen] = useState(false);
  // V2.0.25 — Recently Deleted (tombstones) dialog state.
  const [recentlyDeletedOpen, setRecentlyDeletedOpen] = useState(false);
  // V2.0.85 — Upload-a-floor-plan dialog state. Opened via the "// Upload"
  // toolbar pill OR auto-opened when the URL carries ?upload=1 (Dashboard
  // hand-off from the "Start from file" flow in NewProjectDialog).
  const [uploadOpen, setUploadOpen] = useState(false);
  // V2.0.86 — Upload history dialog (list source_files with download).
  const [historyOpen, setHistoryOpen] = useState(false);
  // V2.0.86 — Before/After share-transform dialog. `shareSnapshotUrl`
  // captures the 3D viewport just BEFORE opening so the split-view has
  // a fresh render regardless of what the user does while the modal is
  // open.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareSnapshotUrl, setShareSnapshotUrl] = useState(null);
  // V1.8.5 — manual cinematic re-record state.
  const [rerecordingCover, setRerecordingCover] = useState(false);
  // V1.7.7 — Share + Save-Snapshot state.
  const [sharing, setSharing] = useState(false);
  // Save-Snapshot chip visibility — flips ON after each successful Simple-
  // Mode chat completion, OFF when the user dismisses or duplicates.
  const [showSnapshotChip, setShowSnapshotChip] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [snapshotSaved, setSnapshotSaved] = useState(false);
  // Suggested follow-up actions parsed by the backend from the AI's reply.
  // Used to render quick-action chips below the chat input. Cleared the
  // moment the user sends the next prompt.
  const [suggestedActions, setSuggestedActions] = useState([]);
  // V1.5.3 Build Plan Preview queue. Set on the first turn of a multi-zone
  // prompt (backend returns `build_queue`); the editor advances the cursor
  // (in_progress → completed → next phase in_progress) as auto-continue
  // chains fire. Cleared when the user starts a fresh prompt.
  const [buildQueue, setBuildQueue] = useState([]);
  // V2.0.81 — Snapshot ref + watchdog + runChatTurn/handleSend all
  // moved into `useChatController`. Editor now consumes `chat.*` for
  // these. The two refs below have to be declared BEFORE the hook
  // call so the hook can capture their identity — their `.current`
  // values are assigned later in this component body (undoLastChange
  // at ~L1090, render-retry inside the chat pipeline itself).
  const undoLastChangeRef = useRef(null);
  const renderRetryRef = useRef(null);

  // V2.0.81 — Start the interaction-telemetry batcher on Editor mount.
  // Idempotent: subsequent Editor mounts (nav between designs) just
  // update the current design id but don't re-add the flush interval.
  useEffect(() => {
    startTelemetryFlush(id);
  }, [id]);

  // V2.0.84 — Marketplace hand-off. If the user came from the Landing
  // prompt marketplace, `first_prompt` is stashed in sessionStorage.
  // Fire it as the first chat once the design has loaded (design is
  // truthy and chat isn't already busy). Clear the storage key so the
  // prompt only runs once per session.
  useEffect(() => {
    if (!design || chatLoading) return;
    let fp;
    try {
      fp =
        sessionStorage.getItem("blueprintai.first_prompt") ||
        localStorage.getItem("blueprintai.first_prompt");
    } catch { fp = null; }
    if (!fp) return;
    try {
      sessionStorage.removeItem("blueprintai.first_prompt");
      localStorage.removeItem("blueprintai.first_prompt");
    } catch { /* noop */ }
    // Small delay so the initial design-load render finishes before we
    // kick off a chat turn.
    const t = setTimeout(() => {
      handleSend(fp, { source: "marketplace" });
    }, 400);
    return () => clearTimeout(t);
  }, [design]);

  // V2.0.85 — If the user arrived via the Dashboard's "Start from file"
  // flow, the URL carries ?upload=1. Auto-open the upload dialog on first
  // Editor mount and strip the param so a browser refresh doesn't
  // re-open it endlessly.
  useEffect(() => {
    if (!design) return;
    if (searchParams.get("upload") === "1") {
      setUploadOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("upload");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design]);

  useEffect(() => {
    (async () => {
      try {
        const r = await getDesign(id);
        setDesign(r.data);
      } catch (e) {
        toast.error("Could not load design");
        navigate("/dashboard");
        return;
      } finally {
        setLoading(false);
      }
      try {
        const c = await getCredits();
        setCredits(c.data.professional_credits);
        setIsAdmin(!!c.data.is_admin);
      } catch (err) { console.debug("[Editor] getCredits failed:", err); }
    })();
  }, [id, navigate]);

  // Auto-save a hero-shot snapshot once the scene has rendered the loaded blueprint.
  // This populates the gallery thumbnail even for designs that pre-date the auto-save.
  useEffect(() => {
    if (!design) return;
    const t = setTimeout(() => {
      persistThumbnail();
    }, 1500);
    return () => clearTimeout(t);
  }, [design?.design_id, design?.blueprint_json]);

  // V1.6 — Auto-engage Drafting Mode for mansion-tier designs (≥6 BD or
  // ≥4000 sqft) the first time a design loads. Fires exactly once per
  // design id; the user retains full control of the toggle thereafter.
  // V1.7: skip the auto-engage when Simple Mode is on (default) — plain
  // users see the friendly 2D Plan view first and don't need the CAD pivot.
  useEffect(() => {
    if (!design || draftingAutoFiredRef.current || simpleMode) return;
    const sqft = Number(design.square_footage) || 0;
    const beds = Number(design.bedrooms) || 0;
    if (sqft >= 4000 || beds >= 6) {
      setDraftingMode(true);
      toast("Drafting Mode auto-engaged for this mansion-tier project", {
        duration: 4500,
      });
    }
    draftingAutoFiredRef.current = true;
  }, [design?.design_id, design?.square_footage, design?.bedrooms, simpleMode]);

  // V1.6 — Top-Down 2D snap. The button auto-engages Drafting Mode (chosen
  // by the user as option 3a) so one click gives the signature blueprint
  // shot. We always also push the camera straight down via the imperative
  // ref method.
  const handleSnapTopDown = useCallback(() => {
    setDraftingMode(true);
    // Defer the camera snap by a tick so the drafting useEffect has time
    // to swap materials + lighting before we orbit overhead.
    setTimeout(() => {
      viewportRef.current?.snapTopDown?.();
    }, 80);
  }, []);

  // V2.0.79 — Stripe checkout + return-URL polling extracted to a
  // dedicated hook. Handles both the outbound redirect and the
  // ?session_id=... poll that fires when the user returns from Stripe.
  // A ref inside the hook is used to always call the freshest callback,
  // so `runSignoffRef` below can defer definition of runSignoff.
  const runSignoffRef = useRef(null);
  const { checkingOut, pollingPayment, startCheckoutSession } = useStripeCheckoutPolling({
    designId: id,
    searchParams,
    setSearchParams,
    onPollStart: () => setSignoffOpen(true),
    onPaid: (data) => {
      setCredits(data.professional_credits);
      setPaywall(null);
      runSignoffRef.current?.();
    },
    onExpired: () => setSignoffOpen(false),
    onFailure: () => setSignoffOpen(false),
  });

  const persistThumbnail = async () => {
    try {
      // forceAllFloors: ensure the hero-shot thumbnail shows the FULL house
      // regardless of the current section-cut state (no roof missing in the gallery).
      const dataUrl = viewportRef.current?.getSnapshotDataUrl({ forceAllFloors: true });
      if (dataUrl) {
        await saveThumbnail(id, dataUrl);
      }
    } catch (err) { console.debug("[Editor] persistThumbnail failed:", err); }
  };

  // V1.8.2 — Cinematic First-View. Record a 3-second, 30° orbit GIF once per
  // design and use it as the dashboard tile thumbnail. Fires only when:
  //   (a) the user is currently on the 3D tab (so the WebGL canvas is mounted
  //       AND visible — toDataURL on a `display:none` canvas returns blanks),
  //   (b) the design has a blueprint with at least one room,
  //   (c) we haven't already produced a GIF thumbnail for this design
  //       (detected by the data:image/gif; prefix),
  //   (d) we haven't already attempted it this session.
  const cinematicAttemptedRef = useRef(false);
  useEffect(() => {
    if (!design || cinematicAttemptedRef.current) return;
    if (canvasView !== "3d") return;
    const floors = design?.blueprint_json?.floors || [];
    const totalRooms = floors.reduce(
      (acc, f) => acc + (Array.isArray(f?.rooms) ? f.rooms.length : 0),
      0,
    );
    if (totalRooms === 0) return;
    if (
      typeof design.thumbnail_data_url === "string" &&
      design.thumbnail_data_url.startsWith("data:image/gif")
    ) {
      cinematicAttemptedRef.current = true;
      return;
    }
    cinematicAttemptedRef.current = true;
    const t = setTimeout(async () => {
      try {
        const frames = await viewportRef.current?.captureCinematicReel?.({
          frames: 24,
          arcDeg: 30,
        });
        if (!frames || frames.length < 2) return;
        // Compress to a tight 480px-wide GIF so the thumbnail fits within
        // Mongo's 16MB document limit + loads fast on the dashboard tile.
        const r = await exportReel(id, frames, 120, 480); // 120ms/frame → 2.9s loop
        const gifB64 = r?.data?.gif_b64;
        if (gifB64) {
          const gifDataUrl = `data:image/gif;base64,${gifB64}`;
          await saveThumbnail(id, gifDataUrl);
          setDesign((prev) =>
            prev ? { ...prev, thumbnail_data_url: gifDataUrl } : prev,
          );
        }
      } catch (_) {
        // Best-effort — silently fall back to the static PNG thumbnail.
      }
    }, 3200);
    return () => clearTimeout(t);
  }, [design?.design_id, design?.blueprint_json?.floors?.length, canvasView]);

  // Run the cinematic tour. If `captureReel`, we start sampling canvas frames
  // every 120ms and POST them to the backend on completion to get an animated
  // GIF the user can download. Total sample budget is ~120 frames (~14s cap).
  const runTour = (captureReel = false) => {
    const wps = buildTourWaypoints(design?.blueprint_json);
    if (!wps || wps.length === 0) {
      toast("Tour needs at least one labelled room");
      return;
    }
    setVisibleFloors([]); // ensure [All] view during the tour
    setTourRunning(true);
    capturedFramesRef.current = [];
    // Small delay so React's clippingPlane clear commits before the first frame.
    setTimeout(() => {
      if (captureReel) {
        // Sample every 180ms — with 120-frame cap, covers up to ~21.6s of tour.
        frameTimerRef.current = setInterval(() => {
          if (capturedFramesRef.current.length >= 120) {
            clearInterval(frameTimerRef.current);
            frameTimerRef.current = null;
            return;
          }
          const f = viewportRef.current?.captureFrame();
          if (f) capturedFramesRef.current.push(f);
        }, 180);
      }
      viewportRef.current?.playTour(wps, async () => {
        setTourRunning(false);
        if (frameTimerRef.current) {
          clearInterval(frameTimerRef.current);
          frameTimerRef.current = null;
        }
        if (!captureReel) {
          toast.success("Tour complete");
          return;
        }
        // Stitch the captured frames into a GIF server-side.
        const frames = capturedFramesRef.current.slice();
        capturedFramesRef.current = [];
        if (frames.length < 2) {
          toast.error("Couldn't capture enough frames");
          return;
        }
        setReelExporting(true);
        toast.loading(`Stitching ${frames.length}-frame reel...`, { id: "reel" });
        try {
          const r = await exportReel(id, frames, 180);
          const { gif_b64, filename, duration_s } = r.data;
          const link = document.createElement("a");
          link.href = `data:image/gif;base64,${gif_b64}`;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast.success(`Reel downloaded (${duration_s}s)`, { id: "reel" });
        } catch (e) {
          toast.error("Reel export failed", { id: "reel" });
        } finally {
          setReelExporting(false);
        }
      });
    }, 60);
  };

  // Dev-only: simulate a Stripe success so QA can verify the post-payment toast.
  const runMockPayment = async () => {
    try {
      const r = await mockPaymentSuccess("concept");
      const { session_id } = r.data;
      toast.success("Mock payment completed");
      // Navigate to the same page with ?session_id=... so Editor's redirect
      // handler fires the normal success flow end-to-end.
      navigate(`/designs/${id}?session_id=${session_id}`, { replace: true });
    } catch (e) {
      toast.error("Mock payment failed (dev endpoint disabled?)");
    }
  };

  // Realistic-render placeholder. Captures a 2K hero-angle snapshot, downloads
  // it, and shows the "coming soon" toast for the high-fidelity AI overlay.
  // The real GenAI bridge (Nano Banana / Gemini Image) will plug into this same
  // entry point in a follow-up.
  const handleEnhanceRender = async () => {
    setEnhanceLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 60));
      const dataUrl = viewportRef.current?.captureProjectSnapshot({ width: 2048, height: 1280 });
      if (!dataUrl) {
        toast.error("Snapshot failed — try again");
        return;
      }
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${(design?.name || "design").replace(/\s+/g, "-").toLowerCase()}-hero-2k.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast("Coming Soon: High-Fidelity AI Overlay", {
        description: "Hero shot saved. Photoreal pass arrives in the next release.",
        duration: 4500,
      });
    } catch (e) {
      toast.error("Could not capture hero shot");
    } finally {
      setEnhanceLoading(false);
    }
  };

  // V1.7.7 — Public read-only share link. Creates (or fetches existing)
  // a slug for this design, copies the share URL to the clipboard, and
  // surfaces a toast with a "Open" action so the user can verify the
  // link instantly.
  const handleShare = useCallback(async () => {
    setSharing(true);
    try {
      const r = await createShareLink(id);
      const slug = r.data?.share_slug;
      const url = `${window.location.origin}/share/${slug}`;
      try {
        await navigator.clipboard?.writeText(url);
        toast.success("Share link copied to clipboard", {
          description: url,
          duration: 6000,
          action: {
            label: "Open",
            onClick: () => window.open(url, "_blank", "noopener"),
          },
        });
      } catch (_) {
        // Clipboard blocked (insecure context, permissions, etc.) — still surface the URL.
        toast.success("Share link ready", {
          description: url,
          duration: 8000,
          action: {
            label: "Open",
            onClick: () => window.open(url, "_blank", "noopener"),
          },
        });
      }
    } catch (e) {
      toast.error("Could not create share link");
    } finally {
      setSharing(false);
    }
  }, [id]);

  // V1.7.7 — Save-snapshot quick action. Duplicates the current design
  // server-side and offers the user a one-tap jump to the new snapshot.
  const handleSaveSnapshot = useCallback(async () => {
    if (snapshotting) return;
    setSnapshotting(true);
    try {
      const r = await duplicateDesign(id);
      const newId = r.data?.design_id;
      const newName = r.data?.name || "Snapshot";
      setSnapshotSaved(true);
      toast.success("Saved to your library", {
        description: newName,
        duration: 6000,
        action: newId
          ? {
              label: "Open",
              onClick: () => navigate(`/designs/${newId}`),
            }
          : undefined,
      });
      // Auto-dismiss the chip after the saved confirmation lingers a moment.
      setTimeout(() => {
        setShowSnapshotChip(false);
        setSnapshotSaved(false);
      }, 2200);
    } catch (e) {
      toast.error("Could not save snapshot");
    } finally {
      setSnapshotting(false);
    }
  }, [id, navigate, snapshotting]);

  // Continuous-build state — when the backend returns auto_continue:true we
  // chain another chat without dropping the LoadingOverlay. The current zone
  // label drives the overlay's headline so the user feels one continuous
  // sequence ("Building Main House → Building Kitchen Wing → 100%").
  const [continuousZone, setContinuousZone] = useState(null);

  // V2.0.81 — Full chat controller (runChatTurn + handleSend + retry
  // cascade + auto-continue + fixture-diff + Undo toasts) extracted
  // to `useChatController`. Editor still owns the state atoms the
  // hook writes into (setSuggestedActions, setBuildQueue, etc.) so
  // that OTHER Editor code paths — Auto-Resolve, sign-off, Reels —
  // can also read/write them without a second hook call.
  const chat = useChatController({
    designId: id,
    design,
    setDesign,
    simpleMode,
    quickMode,
    setQuickMode,
    setQuickModeFlash,
    setSuggestedActions,
    setBuildQueue,
    setContinuousZone,
    setShowSnapshotChip,
    setSnapshotSaved,
    persistThumbnail,
    undoLastChangeRef,
    renderRetryRef,
  });
  const { chatLoading, setChatLoading, previousBlueprintRef, chatLoadingTimeoutRef, runChatTurn, handleSend } = chat;


  // V2.0.31 — Auto-Resolve All: bundles every active code violation into
  // a single ordered punch-list and sends it as one chat turn. Used by
  // both the Live Inspector pill AND the Sign-off pre-flight banner.
  const handleAutoResolveAll = useCallback(async (overrideViolations) => {
    const violations =
      Array.isArray(overrideViolations) && overrideViolations.length > 0
        ? overrideViolations
        : (violationData?.violations || []);
    if (violations.length === 0) {
      toast("No active code violations to resolve");
      return;
    }
    const lines = violations.map((v, i) => {
      const where = v.title || v.rule || "Violation";
      const remedy = v.remedy || "Resolve per IRC reference.";
      return `${i + 1}. (${(v.severity || "").toUpperCase()} · ${v.rule_code || v.category}) ${where} — ${remedy}`;
    });
    const prompt =
      `Please resolve every code violation below in a single response. Apply ALL fixes at once and explain only briefly what changed; do not list violations back to me. Honour IRC 2021 residential provisions.\n\n` +
      lines.join("\n");
    toast.info(`Asking architect to resolve ${violations.length} violation${violations.length === 1 ? "" : "s"}…`);
    try {
      await handleSend(prompt, { source: "auto-resolve", count: violations.length });
    } catch (e) {
      toast.error("Couldn't queue the auto-resolve — please try again.");
    }
  }, [violationData, handleSend]);

  // V2.0.49 — Unified Accessibility mode setter. Modes:
  //   - "off"   : ada_mode=false, audit chip hidden.
  //   - "audit" : ada_mode=false, audit chip surfaces sub-spec doors.
  //   - "on"    : applyAdaCompliance bumps every door + locks drags.
  // Persists `blueprint.ada_inspector_mode` so the choice survives reload.
  const accessibilityMode = useMemo(() => {
    const bp = design?.blueprint_json;
    if (!bp) return "audit";
    if (bp.ada_mode) return "on";
    const m = bp.ada_inspector_mode;
    return m === "off" || m === "audit" || m === "on" ? m : "audit";
  }, [design?.blueprint_json]);

  const handleSetAccessibilityMode = useCallback(async (nextMode) => {
    const bp = design?.blueprint_json;
    if (!bp || nextMode === accessibilityMode) return;
    const wasOn = !!bp.ada_mode;
    const goingOn = nextMode === "on";
    let nextBp;
    if (goingOn) {
      const r = applyAdaCompliance(bp);
      nextBp = { ...r.blueprint, ada_inspector_mode: "on" };
    } else if (wasOn) {
      const r = revertAdaCompliance(bp);
      nextBp = { ...r.blueprint, ada_inspector_mode: nextMode };
    } else {
      nextBp = { ...bp, ada_inspector_mode: nextMode };
    }
    setDesign((d) => ({ ...d, blueprint_json: nextBp }));
    try {
      await updateDesign(id, { blueprint_json: nextBp });
      const violetStyle = {
        background: "linear-gradient(135deg, rgba(79, 70, 229, 0.95), rgba(124, 58, 237, 0.95))",
        color: "#fff",
        border: "1px solid rgba(196, 169, 107, 0.4)",
      };
      if (goingOn) {
        toast.message("♿ Accessibility set to ALWAYS ON — doors bumped to ADA spec", { duration: 4000, style: violetStyle });
      } else if (wasOn) {
        toast.message(`♿ Accessibility set to ${nextMode.toUpperCase()} — doors restored to original widths`, { duration: 4000, style: violetStyle });
      } else if (nextMode === "off") {
        toast.message("♿ Accessibility checks paused", { duration: 2500, style: violetStyle });
      } else {
        toast.message("♿ Accessibility audit re-enabled", { duration: 2500, style: violetStyle });
      }
    } catch {
      toast.error("Couldn't save the Accessibility setting — please try again");
    }
  }, [design?.blueprint_json, id, accessibilityMode]);

  // V2.0.48 — ADA audit dialog state + handlers.
  const [adaAuditOpen, setAdaAuditOpen] = useState(false);
  const adaAudit = useMemo(
    () => auditAdaCompliance(design?.blueprint_json),
    [design?.blueprint_json],
  );

  const handleApplyAdaDoor = useCallback(async (doorId) => {
    const bp = design?.blueprint_json;
    if (!bp || !doorId) return;
    const { blueprint: nextBp, bumped, blocked, target } = bumpDoorToAda(bp, doorId);
    if (blocked) {
      const inches = Math.round((target || 0.813) * 39.3701);
      toast.message(`♿ ADA Boundary: ${doorId} can't reach ${inches}″ — host wall is too short.`, {
        duration: 4000,
        style: {
          background: "linear-gradient(135deg, rgba(79, 70, 229, 0.95), rgba(124, 58, 237, 0.95))",
          color: "#fff",
          border: "1px solid rgba(196, 169, 107, 0.4)",
        },
      });
      return;
    }
    if (!bumped) {
      toast.info(`${doorId} already meets ADA — no change needed`);
      return;
    }
    setDesign((d) => ({ ...d, blueprint_json: nextBp }));
    try {
      await updateDesign(id, { blueprint_json: nextBp });
      const inches = Math.round(bumped.target * 39.3701);
      toast.message(`♿ ${bumped.id} bumped to ${inches}″ ADA clear`, {
        duration: 3500,
        style: {
          background: "linear-gradient(135deg, rgba(79, 70, 229, 0.95), rgba(124, 58, 237, 0.95))",
          color: "#fff",
          border: "1px solid rgba(196, 169, 107, 0.4)",
        },
      });
    } catch {
      toast.error("Couldn't save the ADA bump — please try again");
    }
  }, [design?.blueprint_json, id]);

  const handleApplyAdaAll = useCallback(async () => {
    const bp = design?.blueprint_json;
    if (!bp) return;
    let working = bp;
    let appliedCount = 0;
    let blockedCount = 0;
    for (const v of (adaAudit.violations || [])) {
      const { blueprint: next, bumped, blocked } = bumpDoorToAda(working, v.door_id);
      if (blocked) {
        blockedCount += 1;
        continue;
      }
      if (bumped) {
        appliedCount += 1;
        working = next;
      }
    }
    if (appliedCount === 0 && blockedCount === 0) {
      toast.info("Nothing to apply — every door already meets ADA");
      return;
    }
    setDesign((d) => ({ ...d, blueprint_json: working }));
    try {
      await updateDesign(id, { blueprint_json: working });
      const msg = blockedCount > 0
        ? `♿ Applied ADA to ${appliedCount} door${appliedCount === 1 ? "" : "s"} · ${blockedCount} blocked (host wall too short)`
        : `♿ Applied ADA to ${appliedCount} door${appliedCount === 1 ? "" : "s"}`;
      toast.message(msg, {
        duration: 4000,
        style: {
          background: "linear-gradient(135deg, rgba(79, 70, 229, 0.95), rgba(124, 58, 237, 0.95))",
          color: "#fff",
          border: "1px solid rgba(196, 169, 107, 0.4)",
        },
      });
    } catch {
      toast.error("Couldn't save the ADA batch — please try again");
    }
  }, [design?.blueprint_json, id, adaAudit]);

  // V2.0.48 — Combo: Apply ADA + Resolve Code. Chains the per-door ADA
  // bumps with the existing IRC auto-resolver in one click.
  const handleResolveAllWithAda = useCallback(async () => {
    await handleApplyAdaAll();
    // The IRC auto-resolver reads violations from violationData; the
    // ADA bumps don't affect the IRC violations list, so we can call
    // handleAutoResolveAll immediately with the existing list.
    const v = (violationData?.violations || []);
    if (v.length === 0) {
      toast.info("No code violations to resolve — ADA bumps applied");
      return;
    }
    await handleAutoResolveAll(v);
  }, [handleApplyAdaAll, handleAutoResolveAll, violationData]);


  // V2.0.35 — Extracted so both ViewOptionsPanel and LocalCodeDialog
  // can save the build-site address without duplicating logic.
  const handleSaveLocation = useCallback(async (meta) => {
    try {
      const r = await updateDesign(id, { build_site_metadata: meta });
      setDesign((prev) => ({
        ...prev,
        blueprint_json: {
          ...(prev?.blueprint_json || {}),
          build_site_metadata: meta,
        },
      }));
      toast.success("Location saved");
      return r.data;
    } catch (e) {
      toast.error("Could not save location");
      throw e;
    }
  }, [id]);

  // Revert blueprint + the latest user/assistant exchange. Used by both the
  // post-chat Undo toast action and the render-error auto-recovery.
  const undoLastChange = useCallback(async (snapshotBlueprint) => {
    if (!snapshotBlueprint) {
      toast("Nothing to undo");
      return;
    }
    setDesign((d) => {
      const hist = [...(d?.chat_history || [])];
      while (hist.length && hist[hist.length - 1].role === "assistant") hist.pop();
      while (hist.length && hist[hist.length - 1].role === "user") hist.pop();
      return { ...d, blueprint_json: snapshotBlueprint, chat_history: hist };
    });
    try {
      await updateDesign(id, { blueprint_json: snapshotBlueprint });
      toast.success("Reverted to previous design");
    } catch (_) {
      toast.error("Reverted in this view, but couldn't save the rollback");
    }
  }, [id]);
  // V2.0.81 — undoLastChangeRef declared near the top so useChatController
  // can capture it. Assign latest undoLastChange each render so toast
  // action callbacks always call the freshest closure.
  undoLastChangeRef.current = undoLastChange;

  // Render-error recovery: ThreeViewport calls this when its blueprint
  // rebuild throws. V1.5.1 adds a SILENT low-detail retry — we re-issue the
  // last prompt with low_detail:true so the user sees something rather than
  // a hard error. If the retry ALSO fails, fall back to the Undo toast.
  const renderRetryAttemptedRef = useRef(false);
  const handleRenderError = useCallback(async (err) => {
    setChatLoading(false);
    setContinuousZone(null);
    if (chatLoadingTimeoutRef.current) {
      clearTimeout(chatLoadingTimeoutRef.current);
      chatLoadingTimeoutRef.current = null;
    }
    const snapshot = previousBlueprintRef.current;
    const lastPrompt = renderRetryRef.current?.lastPrompt;
    console.warn("[Editor] viewport render error:", err);

    // Silent retry — once per render error, only when we have a prompt to
    // re-issue. Bypasses the visible error toast in favor of a quiet status.
    if (!renderRetryAttemptedRef.current && lastPrompt) {
      renderRetryAttemptedRef.current = true;
      setChatLoading(true);
      try {
        const data = await runChatTurn(lastPrompt, { lowDetail: true });
        const actions = Array.isArray(data?.suggested_actions) ? data.suggested_actions : [];
        setSuggestedActions(actions);
        toast.success("Recovered with a simpler render");
      } catch (_) {
        toast.error("Render failed — geometry too complex", {
          duration: 12000,
          action: snapshot
            ? { label: "Undo", onClick: () => undoLastChangeRef.current?.(snapshot) }
            : undefined,
        });
      } finally {
        setChatLoading(false);
        // Reset the gate so a future, unrelated render error can retry once.
        setTimeout(() => { renderRetryAttemptedRef.current = false; }, 5000);
      }
      return;
    }

    toast.error("Render failed — geometry too complex", {
      duration: 12000,
      action: snapshot
        ? { label: "Undo", onClick: () => undoLastChangeRef.current?.(snapshot) }
        : undefined,
    });
  }, [runChatTurn]);

  // Inner: actually run the sign-off (assuming credit available). Used by:
  //  - direct click when credits > 0
  //  - after Stripe redirect once payment is confirmed
  // V1.9.1 — Now accepts an optional reviewer email to CC. The primary
  // recipient is always the signed-in user; the reviewer is opt-in.
  const runSignoff = async (reviewerEmail = null) => {
    setSignoffLoading(true);
    try {
      const dataUrl = viewportRef.current?.getSnapshotDataUrl({ forceAllFloors: true });
      const r = await requestSignoff(id, dataUrl, reviewerEmail);
      setSignoffResult(r.data);
      setDesign((d) => ({
        ...d,
        status: "sent_for_review",
        thumbnail_data_url: dataUrl,
      }));
      try {
        const c = await getCredits();
        setCredits(c.data.professional_credits);
      } catch (err) { console.debug("[Editor] getCredits post-signoff failed:", err); }
      toast.success(
        reviewerEmail
          ? `Package emailed — you & ${reviewerEmail}`
          : "Package emailed to you",
      );
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        // Paywall triggered
        const info = typeof detail === "object" ? detail : {};
        setPaywall({
          price_usd: info.price_usd ?? 19,
          message: info.message,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Sign-off failed");
      }
    } finally {
      setSignoffLoading(false);
    }
  };

  const openSignoff = async () => {
    setSignoffResult(null);
    setPaywall(null);
    setSignoffOpen(true);
    // Pre-check credits to decide whether to show paywall directly.
    // V2.0.62 — admins (ADMIN_EMAILS allowlist) skip the paywall front-run
    // entirely; the backend signoff route bypasses the credit gate too.
    try {
      const c = await getCredits();
      setCredits(c.data.professional_credits);
      const admin = !!c.data.is_admin;
      setIsAdmin(admin);
      if (!admin && (c.data.professional_credits || 0) < 1) {
        setPaywall({ price_usd: 19 });
      }
    } catch (err) {
      console.debug("[Editor] credits pre-check failed:", err);
      // proceed; backend will 402 if needed
    }
  };

  const handleConfirm = async (reviewerEmail = null) => {
    await runSignoff(reviewerEmail);
  };

  // V2.0.79 — Delegates to the hook so return-URL polling can call
  // the freshest runSignoff via `runSignoffRef`. Also expose the
  // handler under the old name for JSX call sites.
  const handleCheckout = startCheckoutSession;
  // Keep the ref current so the polling effect (which fires at t=0)
  // can invoke the latest runSignoff even though runSignoff itself is
  // defined further down the component body.
  runSignoffRef.current = runSignoff;

  if (loading) {
    return (
      <div
        className="min-h-screen grid place-items-center bg-zinc-950"
        data-testid="editor-loading"
      >
        <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 architecting-dot">
          Loading workspace...
        </div>
      </div>
    );
  }
  if (!design) return null;

  const isReviewed = design.status === "sent_for_review";

  return (
    <div className="h-[100dvh] min-h-screen flex flex-col bg-zinc-950 overflow-hidden">
      <HeaderBar
        variant="editor"
        designName={design.name}
        rightSlot={
          <>
            <button
              onClick={() => navigate("/dashboard")}
              className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-50 transition-colors px-3 py-2 border border-zinc-800 hover:border-zinc-600"
              data-testid="editor-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Projects
            </button>
            {typeof credits === "number" && credits > 0 && !isReviewed && (
              <div
                className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-brass-400 px-3 py-2 border border-brass-700/50 bg-brass-950/20"
                data-testid="editor-credits-badge"
              >
                <Sparkles className="w-3 h-3" /> Professional Credits: {credits}
              </div>
            )}
            {isReviewed ? (
              <div
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-brass-400 px-3 py-2 border border-brass-700/60 bg-brass-950/20"
                data-testid="editor-status-badge"
              >
                <BadgeCheck className="w-3.5 h-3.5" /> Sent for Review
              </div>
            ) : (
              <button
                onClick={handleShare}
                disabled={sharing}
                className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-brass-300 px-3 py-2 border border-brass-700/60 bg-brass-950/20 hover:bg-brass-900/40 disabled:opacity-50"
                data-testid="share-link-btn"
                title="Create a public read-only share link"
              >
                {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                // Share
              </button>
            )}
            {!isReviewed && (
              <button
                onClick={() => setUploadOpen(true)}
                title="Upload a floor plan sketch, PDF, or CAD file — the AI will parse it into an editable blueprint"
                data-testid="upload-blueprint-open-btn"
                className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-brass-300 px-3 py-2 border border-brass-700/60 bg-brass-950/20 hover:bg-brass-900/40 transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload Sketch
              </button>
            )}
            {!isReviewed && (
              <button
                onClick={() => setHistoryOpen(true)}
                title="View all files you've uploaded for this design"
                data-testid="upload-history-open-btn"
                className="hidden lg:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 px-3 py-2 border border-zinc-800 bg-zinc-900/40 hover:border-brass-700/60 hover:text-brass-300 transition-colors"
              >
                <History className="w-3.5 h-3.5" />
                History
              </button>
            )}
            {!isReviewed && (
              <button
                onClick={() => {
                  // Grab the freshest 3D snapshot BEFORE opening the
                  // dialog. If the viewport isn't mounted yet or the
                  // snapshot fails, pass null — the endpoint will show
                  // a placeholder card.
                  try {
                    const dataUrl = viewportRef.current?.getSnapshotDataUrl?.({ forceAllFloors: true });
                    // V2.0.86 hardening — an empty/uninitialized canvas returns
                    // "data:," which the backend rejects with a 400. Only forward
                    // real image data URLs; otherwise pass null so the backend
                    // renders the placeholder card.
                    const safe = typeof dataUrl === "string" && dataUrl.startsWith("data:image/") ? dataUrl : null;
                    setShareSnapshotUrl(safe);
                  } catch { setShareSnapshotUrl(null); }
                  setShareOpen(true);
                }}
                title="Post your before/after transformation on LinkedIn or Twitter"
                data-testid="share-transform-open-btn"
                className="hidden lg:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 px-3 py-2 border border-zinc-800 bg-zinc-900/40 hover:border-brass-700/60 hover:text-brass-300 transition-colors"
              >
                <Share2 className="w-3.5 h-3.5" />
                Before/After
              </button>
            )}
            {!isReviewed && (
              <button
                onClick={() => setRecentlyDeletedOpen(true)}
                title="Recently deleted items — restore with one click"
                data-testid="recently-deleted-open-btn"
                className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-brass-300 px-3 py-2 border border-zinc-800 hover:border-brass-700/60 transition-colors"
              >
                <Undo2 className="w-3.5 h-3.5" />
                Recently Deleted
              </button>
            )}
            {!isReviewed && (
              <button
                onClick={openSignoff}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-mono text-[10px] uppercase tracking-widest px-4 py-2 transition-colors"
                data-testid="signoff-request-btn"
              >
                <FileSignature className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Purchase My Package</span>
                <span className="sm:hidden">Purchase</span>
              </button>
            )}
            {process.env.NODE_ENV !== "production" && (
              <button
                onClick={runMockPayment}
                className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-fuchsia-300 px-3 py-2 border border-fuchsia-700/50 bg-fuchsia-950/20 hover:bg-fuchsia-900/30"
                data-testid="dev-mock-payment-btn"
                title="Dev only — simulates a Stripe success redirect"
              >
                // Test Payment
              </button>
            )}
          </>
        }
      />

      <div className="flex-1 flex min-h-0">
      <div
        className={
          "flex-1 relative min-h-0 " +
          /* V2.0.5 — reserve room for the MobileChatDock at the bottom
             (~140 px collapsed = preview + chips + input + safe-area)
             so the floor plan + bottom toolbar aren't covered by the
             chat-first dock on phones. Tablet+ keeps the original
             layout (chat lives in the left rail). */
          (isMobile ? "pb-[140px]" : "")
        }
      >
        {/* V1.7 — Canvas view tabs (top-center). Just 3D / Plan switcher
            so it stays compact and doesn't overlap the FLOOR selector
            in the top-left corner. The auxiliary "Imagine / Code Check /
            Energy" actions live in a SEPARATE bar on the top-right.
            V2.0.4 — Hidden on mobile (phones lock to PLAN view). */}
        {!isMobile && (
        <div
          data-testid="canvas-tabs"
          className="absolute top-3 left-1/2 -translate-x-1/2 z-20 inline-flex border border-zinc-700 bg-zinc-950/85 backdrop-blur"
        >
          <button
            type="button"
            data-testid="canvas-tab-3d"
            onClick={() => setCanvasView("3d")}
            aria-pressed={canvasView === "3d"}
            className={
              "px-3 py-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors " +
              (canvasView === "3d"
                ? "bg-brass-600 text-white"
                : "text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800")
            }
          >
            <Box className="w-3 h-3" /> // 3D
          </button>
          <button
            type="button"
            data-testid="canvas-tab-plan"
            onClick={() => setCanvasView("plan")}
            aria-pressed={canvasView === "plan"}
            className={
              "px-3 py-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border-l border-zinc-700 transition-colors " +
              (canvasView === "plan"
                ? "bg-brass-600 text-white"
                : "text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800")
            }
          >
            <LayoutPanelTop className="w-3 h-3" /> // Plan
          </button>
        </div>
        )}

        {/* V1.9.8 — Auxiliary actions bar (top-right, OFFSET LEFT of the
            VIEW OPTIONS pill which lives at top-4 right-4 z-30).
            V2.0.4 — On mobile the bar collapses into a single overflow
            (•••) button that opens a vertical menu, keeping the canvas
            usable on a phone screen. */}
        {isMobile ? (
          <div
            data-testid="canvas-actions-mobile"
            className="absolute top-3 right-3 z-30"
          >
            <button
              type="button"
              data-testid="canvas-actions-more-btn"
              onClick={() => setMobileActionsOpen((v) => !v)}
              aria-expanded={mobileActionsOpen}
              className="px-3 py-2 border border-zinc-700 bg-zinc-950/90 text-zinc-200 font-mono text-[11px] uppercase tracking-widest shadow-md min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
            >
              •••
            </button>
            {mobileActionsOpen && (
              <div
                data-testid="canvas-actions-mobile-menu"
                className="mt-1 flex flex-col border border-zinc-700 bg-zinc-950/95 backdrop-blur shadow-lg min-w-[170px]"
              >
                <button
                  type="button"
                  data-testid="canvas-renders-btn"
                  onClick={() => { setRendersOpen(true); setMobileActionsOpen(false); }}
                  className="px-3 py-2.5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-orange-300 hover:bg-orange-950/40"
                >
                  <ImageIcon className="w-3.5 h-3.5" /> // Imagine
                </button>
                <button
                  type="button"
                  data-testid="canvas-code-check-btn"
                  onClick={() => { setCodeViolationsOpen(true); setMobileActionsOpen(false); }}
                  className="px-3 py-2.5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border-t border-zinc-800 text-emerald-300 hover:bg-emerald-950/40"
                >
                  <ShieldCheck className="w-3.5 h-3.5" /> // Code Check
                </button>
                <button
                  type="button"
                  data-testid="canvas-energy-btn"
                  onClick={() => { setEnergyScoreOpen(true); setMobileActionsOpen(false); }}
                  className="px-3 py-2.5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border-t border-zinc-800 text-yellow-300 hover:bg-yellow-950/40"
                >
                  <Zap className="w-3.5 h-3.5" /> // Energy
                </button>
                <button
                  type="button"
                  data-testid="canvas-local-code-btn"
                  onClick={() => { setLocalCodeOpen(true); setMobileActionsOpen(false); }}
                  className="px-3 py-2.5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border-t border-zinc-800 text-brass-300 hover:bg-brass-950/40"
                >
                  <MapPin className="w-3.5 h-3.5" /> // Local Code
                </button>
              </div>
            )}
          </div>
        ) : (
        <div
          data-testid="canvas-actions"
          className="absolute top-3 right-44 z-20 inline-flex border border-zinc-700 bg-zinc-950/85 backdrop-blur"
        >
          <button
            type="button"
            data-testid="canvas-renders-btn"
            onClick={() => setRendersOpen(true)}
            className="px-3 py-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:text-orange-200 hover:bg-orange-950/40 transition-colors"
          >
            <ImageIcon className="w-3 h-3" /> // Imagine
          </button>
          <button
            type="button"
            data-testid="canvas-code-check-btn"
            onClick={() => setCodeViolationsOpen(true)}
            title="Run a residential code-compliance scan (IRC 2021)"
            className="px-3 py-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border-l border-zinc-700 text-emerald-300 hover:text-emerald-200 hover:bg-emerald-950/40 transition-colors"
          >
            <ShieldCheck className="w-3 h-3" /> // Code Check
          </button>
          <button
            type="button"
            data-testid="canvas-energy-btn"
            onClick={() => setEnergyScoreOpen(true)}
            title="Estimate annual heating + cooling load + a 0..100 efficiency score"
            className="px-3 py-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border-l border-zinc-700 text-yellow-300 hover:text-yellow-200 hover:bg-yellow-950/40 transition-colors"
          >
            <Zap className="w-3 h-3" /> // Energy
          </button>
          <button
            type="button"
            data-testid="canvas-local-code-btn"
            onClick={() => setLocalCodeOpen(true)}
            title="See the building codes, IECC zone, and hazard overlays that apply to this design's site"
            className="px-3 py-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border-l border-zinc-700 text-brass-300 hover:text-brass-200 hover:bg-brass-950/40 transition-colors"
          >
            <MapPin className="w-3 h-3" /> // Local Code
          </button>
        </div>
        )}

        {/* V1.9.1 — Simple Mode is LOCKED on (toggle removed). A status
            pill lives in the // VIEW OPTIONS panel so users still know
            which mode they're in without cluttering the canvas. */}

        {/* Full-screen 3D viewport as the background layer */}
        <div className={canvasView === "3d" ? "absolute inset-0" : "hidden"}>
          <ThreeViewport
            ref={viewportRef}
            blueprint={design.blueprint_json}
            isLoading={false}
            style={design.style}
            visibleFloors={visibleFloors}
            currentFloor={currentFloor}
            onFloorsAvailable={setFloorLevels}
            timeOfDay={timeOfDay}
            showInteriorWalls={showInteriorWalls}
            xrayInteriorWalls={xrayInteriorWalls}
            draftingMode={draftingMode}
            onRenderError={handleRenderError}
            onMiniMapTeleport={(room) => {
              // V1.8.0 — fire analytics ping (best-effort). Skip throwing so
              // an analytics outage never breaks the teleport itself.
              postAnalyticsEvent({
                design_id: id,
                event: "minimap.teleport",
                meta: { room_id: room?.id, room_name: room?.name },
              }).catch(() => {});
            }}
          />
        </div>

        {/* V1.7 — 2D Floor Plan canvas (Simple Mode primary deliverable).
            V1.7.6: shifted right of the 400-px chat rail on lg+ so the plan
            doesn't render behind the dark chat panel. */}
        {canvasView === "plan" && (
          <div className="absolute inset-0 lg:left-[400px] bg-white">
            <FloorPlan2D
              blueprint={design.blueprint_json}
              designId={id}
              floorIndex={currentFloor}
              onChangeFloor={setCurrentFloor}
              onCheckout={handleCheckout}
              violations={violationData?.violations}
              measurementSystem={design?.measurement_system || "imperial"}
              onPreviewBlueprint={(next) => {
                // V1.9.3 — cheap local-only update used during smooth
                // pointer-drag / resize / opening-drag. The final
                // authoritative PUT fires on pointerup via onUpdateBlueprint.
                setDesign((d) => ({ ...d, blueprint_json: next }));
              }}
              onUpdateBlueprint={async (next) => {
                setDesign((d) => ({ ...d, blueprint_json: next }));
                try {
                  await updateDesign(id, { blueprint_json: next });
                } catch (e) {
                  toast.error("Couldn't save symbol — please try again");
                }
              }}
            />
          </div>
        )}

        {/* V1.6 — Blueprint Legend overlay. Bottom-right CAD title-block,
            visible only when Drafting Mode is engaged (and 3D is active). */}
        {draftingMode && canvasView === "3d" && (
          <BlueprintLegend
            projectName={design.name}
            style={design.style}
            scale="1:100"
          />
        )}

        {/* V1.2 staged progress bar — overlays the top of the viewport while
            chatLoading is true. Animates 0→90% in 12s, snaps to 100% on
            completion. Replaces the in-place 'Architecting' indicator. */}
        <LoadingOverlay loading={chatLoading} continuousLabel={continuousZone} />

        {/* Keyboard shortcut hints — auto-pops on first visit, summon via `?`.
            Wires actual key actions: X → X-Ray, 1/2/3 → section-cut floor,
            0 → all floors, Space → cinematic tour. Ignores keys typed
            inside chat inputs. */}
        <ShortcutsOverlay
          onToggleXRay={() => setXrayInteriorWalls((v) => !v)}
          onSelectFloor={(level) => setVisibleFloors(level == null ? [] : [level])}
          onPlayTour={() => runTour(false)}
        />

        {/* Floating view options (top-right): Time of Day + Section-cut / floors + Play Tour */}
        <ViewOptionsPanel
          simpleMode={simpleMode}
          measurementSystem={design?.measurement_system || "imperial"}
          onChangeMeasurementSystem={async (system) => {
            setDesign((d) => ({ ...d, measurement_system: system }));
            try {
              await updateDesign(id, { measurement_system: system });
              toast.success(`Switched to ${system === "metric" ? "Metric (cm/m)" : "Imperial (ft/in)"}`);
            } catch {
              toast.error("Couldn't save unit preference");
            }
          }}
          timeOfDay={timeOfDay}
          onTimeOfDayChange={setTimeOfDay}
          floorLevels={floorLevels}
          visibleFloors={visibleFloors}
          onVisibleFloorsChange={setVisibleFloors}
          tourRunning={tourRunning}
          reelExporting={reelExporting}
          onPlayTour={() => runTour(false)}
          onExportReel={() => runTour(true)}
          onEnhance={handleEnhanceRender}
          enhanceLoading={enhanceLoading}
          showInteriorWalls={showInteriorWalls}
          onToggleInteriorWalls={setShowInteriorWalls}
          xrayInteriorWalls={xrayInteriorWalls}
          onToggleXrayInteriorWalls={setXrayInteriorWalls}
          draftingMode={draftingMode}
          onToggleDraftingMode={setDraftingMode}
          onSnapTopDown={handleSnapTopDown}
          onResetView={() => {
            viewportRef.current?.resetView?.();
            toast("View reset", { duration: 2500 });
          }}
          onRerecordCover={async () => {
            if (rerecordingCover) return;
            // Forces a re-capture regardless of existing GIF. Useful after
            // major layout changes (e.g. adding a 2nd floor) so the dashboard
            // tile stays in sync.
            if (canvasView !== "3d") {
              toast.info("Switch to the 3D tab first to capture the cover.");
              return;
            }
            setRerecordingCover(true);
            try {
              const frames = await viewportRef.current?.captureCinematicReel?.({
                frames: 24,
                arcDeg: 30,
              });
              if (!frames || frames.length < 2) {
                toast.error("Couldn't capture the scene — make sure the 3D view is loaded.");
                return;
              }
              const r = await exportReel(id, frames, 120, 480);
              const gifB64 = r?.data?.gif_b64;
              if (gifB64) {
                const gifDataUrl = `data:image/gif;base64,${gifB64}`;
                await saveThumbnail(id, gifDataUrl);
                setDesign((prev) =>
                  prev ? { ...prev, thumbnail_data_url: gifDataUrl } : prev,
                );
                toast.success("Dashboard cover updated", { duration: 4000 });
              } else {
                toast.error("Cover encoding failed");
              }
            } catch (_) {
              toast.error("Cover re-record failed");
            } finally {
              setRerecordingCover(false);
            }
          }}
          rerecordingCover={rerecordingCover}
          buildSiteMetadata={design?.blueprint_json?.build_site_metadata}
          onSaveLocation={handleSaveLocation}
        />

        {/* Chat rail (desktop) / bottom drawer (mobile). On mobile the
            persistent ChatDrawer (which renders a floating mic FAB on
            small screens) is REPLACED by the V2.0.5 MobileChatDock — a
            chat-first bottom panel. We hide the legacy mobile FAB by
            forcing ChatDrawer to its desktop-only branch via the
            isMobile flag below. */}
        {!isMobile && (
        <ChatDrawer
          messages={design.chat_history || []}
          onSend={handleSend}
          loading={chatLoading}
          designSummary={{
            style: design.style,
            square_footage: design.square_footage,
            bedrooms: design.bedrooms,
            bathrooms: design.bathrooms,
          }}
          suggestedActions={suggestedActions}
          onClearSuggestions={() => setSuggestedActions([])}
          buildQueue={buildQueue}
          snapshotChip={{
            visible: showSnapshotChip,
            saving: snapshotting,
            saved: snapshotSaved,
            onSave: handleSaveSnapshot,
            onDismiss: () => setShowSnapshotChip(false),
          }}
          quickMode={quickMode}
          onToggleQuickMode={toggleQuickMode}
          quickModeFlash={quickModeFlash}
        />
        )}
        {isMobile && (
          <MobileChatDock
            messages={design.chat_history || []}
            onSend={handleSend}
            loading={chatLoading}
            suggestedActions={suggestedActions}
            onClearSuggestions={() => setSuggestedActions([])}
          />
        )}
      </div>

      {/* V2.0.29 — Right-rail Live Inspector. Desktop-only; collapsible. */}
      <LiveInspectorRail
        designId={id}
        blueprint={design.blueprint_json}
        violationData={violationData}
        loadingViolations={loadingViolations}
        ifcValidation={ifcValidation}
        ifcValidationLoading={ifcValidationLoading}
        autoResolving={chatLoading}
        onAutoResolveAll={handleAutoResolveAll}
        accessibilityMode={accessibilityMode}
        onSetAccessibilityMode={handleSetAccessibilityMode}
        onOpenAdaAudit={() => setAdaAuditOpen(true)}
        onResolveAllWithAda={handleResolveAllWithAda}
      />
      </div>

      <SignoffDialog
        open={signoffOpen}
        onClose={() => {
          setSignoffOpen(false);
          setSignoffResult(null);
          setPaywall(null);
        }}
        onConfirm={handleConfirm}
        onCheckout={handleCheckout}
        design={design}
        result={signoffResult}
        loading={signoffLoading}
        paywall={paywall}
        credits={credits}
        checkingOut={checkingOut}
        userEmail={user?.email}
        pollingPayment={pollingPayment}
        autoResolving={chatLoading}
        onAutoResolveAll={() => {
          // V2.0.33 — close the Sign-off dialog so the user sees the
          // chat progress while the architect resolves the punch-list;
          // they can re-open Sign-off after the toast confirms.
          setSignoffOpen(false);
          handleAutoResolveAll();
        }}
        onReviewViolations={() => {
          setSignoffOpen(false);
          setCodeViolationsOpen(true);
        }}
      />

      {/* V1.7 — AI Renders panel (Studio tier paywall) */}
      <AIRendersPanel
        open={rendersOpen}
        onClose={() => setRendersOpen(false)}
        designId={id}
        credits={credits}
        onCheckout={handleCheckout}
        onCoverSet={(dataUrl) =>
          setDesign((prev) => prev ? { ...prev, thumbnail_data_url: dataUrl } : prev)
        }
      />

      {/* V1.9.5 — Building code-compliance scan dialog. */}
      <CodeViolationsDialog
        open={codeViolationsOpen}
        onClose={() => setCodeViolationsOpen(false)}
        designId={id}
        onAutoFix={async (v) => {
          // V1.9.6 — translate a violation into a targeted chat turn so
          // the AI architect can repair the issue without the user typing.
          const obstacleName = v.location?.obstacle;
          const prompts = {
            egress: `Add an operable egress window with at least 20" clear width to ${v.title.split(": ").pop()}. Honour IRC R310.`,
            circulation: `Widen the offending element to meet IRC ${v.rule_code} — at least 36" of clear width.`,
            headroom: `Raise the ceiling height of the affected floor to at least 2.03 m (6'-8") for stair headroom per IRC R311.7.2.`,
            fixture: `Increase the floor area of ${v.title.split(": ").pop()} to at least 70 sq ft per IRC R304.1.`,
            // V1.9.7 — door-swing clash now identifies the specific
            // symbol/fixture blocking the swing so the AI knows what to move.
            swing: obstacleName
              ? `Move the ${obstacleName} out of the door's 30" swing arc. ${v.detail} Reposition the ${obstacleName} at least 0.8 m away from the door pivot or switch the door to a pocket/sliding type.`
              : `Address the door swing clash: ${v.title}. ${v.remedy}`,
          };
          const prompt = prompts[v.category] || `Address the code violation: ${v.title}. ${v.remedy}`;
          setCodeViolationsOpen(false);
          toast.info(`Asking architect to: ${v.title}`);
          try {
            await handleSend(prompt);
          } catch (e) {
            toast.error("Couldn't queue the fix — please try again.");
          }
        }}
      />

      {/* V1.9.6 — Envelope energy score dialog. */}
      <EnergyScoreDialog
        open={energyScoreOpen}
        onClose={() => setEnergyScoreOpen(false)}
        designId={id}
      />

      {/* V2.0.11 — Local code (zip-aware site rules) dialog. */}
      <LocalCodeDialog
        open={localCodeOpen}
        designId={id}
        currentZip={design?.blueprint_json?.build_site_metadata?.zip_code || ""}
        currentState={design?.blueprint_json?.build_site_metadata?.state || ""}
        currentCity={design?.blueprint_json?.build_site_metadata?.city || ""}
        onSaveLocation={handleSaveLocation}
        onClose={() => setLocalCodeOpen(false)}
        onRunCodeCheck={() => {
          setLocalCodeOpen(false);
          setCodeViolationsOpen(true);
        }}
      />

      {/* V2.0.39 — Architect Hand-off (B2B referral flywheel). */}
      <ArchitectHandoffDialog
        open={architectHandoffOpen}
        designId={id}
        designName={design?.name}
        onClose={() => setArchitectHandoffOpen(false)}
      />

      {/* V2.0.25 — Recently Deleted (tombstones) safety-net dialog. */}
      <RecentlyDeletedDialog
        open={recentlyDeletedOpen}
        onClose={() => setRecentlyDeletedOpen(false)}
        designId={id}
        onRestored={(fresh) => {
          if (fresh) setDesign(fresh);
        }}
      />
      {/* V2.0.85 — Upload-a-floor-plan dialog. */}
      <UploadBlueprintDialog
        open={uploadOpen}
        designId={id}
        currentRoomCount={countBlueprintRooms(design?.blueprint_json)}
        currentFloorCount={Math.max(1, (design?.blueprint_json?.floors?.length) || 1)}
        onClose={() => setUploadOpen(false)}
        onApply={async ({ blueprint, mode }) => {
          const current = design?.blueprint_json || { floors: [], materials: {} };
          const next = mode === "replace"
            ? blueprint
            : mergeParsedBlueprint(current, blueprint);
          setDesign((d) => ({ ...d, blueprint_json: next }));
          const r = await updateDesign(id, { blueprint_json: next });
          if (r?.data?.blueprint_json) {
            setDesign((d) => ({ ...d, blueprint_json: r.data.blueprint_json }));
          }
        }}
      />
      {/* V2.0.48 — ADA Audit dialog */}
      <AdaAuditDialog
        open={adaAuditOpen}
        onOpenChange={setAdaAuditOpen}
        audit={adaAudit}
        onApplyDoor={handleApplyAdaDoor}
        onApplyAll={handleApplyAdaAll}
      />
      {/* V2.0.86 — Upload history + Before/After share dialogs. */}
      <UploadHistoryDialog
        open={historyOpen}
        designId={id}
        onClose={() => setHistoryOpen(false)}
      />
      <ShareTransformDialog
        open={shareOpen}
        designId={id}
        snapshotDataUrl={shareSnapshotUrl}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
}
